// Styles for use in the lineage/metadata info pane.

import { TabStyles } from '@tableau/widgets';
import {
  AccessibleHyperLinkBlue,
  AccessibleHyperLinkBlueFocus,
  AccessibleHyperLinkBlueHover,
  cssClass,
  divWithStyle,
  Style,
  userSelectStyle,
} from '../../../data-catalog-shared';

const Colors = TabStyles.Colors;
const Spacing = TabStyles.Spacing;
const Sizing = TabStyles.Sizing;
const Typography = TabStyles.Typography;

export const dataDetailsGutterWidth = 20;
export const dataDetailsContentWidth = 281;

const lineagePanelContainerStyles: Style = {
  position: 'absolute',
  top: '0px',
  bottom: '0px',
  left: '0px',
  right: '0px',
  borderLeftStyle: 'solid',
  borderLeftWidth: Sizing.BorderWidth,
  borderLeftColor: Colors.L6,
  backgroundColor: Colors.White,
  color: Typography.Color.Primary,
  $nest: {
    'a': {
      textDecoration: 'none',
      outline: 0,
      color: AccessibleHyperLinkBlue,
      $nest: {
        '&:hover': {
          textDecoration: 'underline',
          color: AccessibleHyperLinkBlueHover
        },
        '&:focus': {
          textDecoration: 'underline',
          color: AccessibleHyperLinkBlueFocus
        }
      }
    }
  }
};

export const LineagePanelContainer = divWithStyle('lineagePanel', lineagePanelContainerStyles);

export const LoadingErrorMessage = divWithStyle('loadingErrorMessage', {
  marginTop: TabStyles.Spacing.LargeSpace,
  marginBottom: TabStyles.Spacing.MediumSpace,
  color: Typography.Color.Primary,
  fontSize: Typography.Sizing.Default,
  whiteSpace: 'pre-line'
});

export const LoadingErrorCode = divWithStyle('loadingErrorCode', {
  marginBottom: TabStyles.Spacing.ExtraLargeSpace,
  color: Typography.Color.Secondary,
  fontSize: Typography.Sizing.Small
});

export const loadingIndicator: string = cssClass('loadingIndicator', {
  textAlign: 'center',
  margin: Spacing.ExtraLargeSpace,
  marginTop: '84px',
  fontFamily: Typography.FontFamily,
  color: Typography.Color.Primary
});

// Sizing constants are potentially undefined.
const TitleBarHeight: number = Sizing.RowHeight || 0;
const TitleBarPaddingTop: number = Spacing.SmallSpace || 0;
const TitleBarPaddingBottom: number = Spacing.SmallSpace || 0;
const TitleBarBorderBottomWidth = 1;

export const TitleBarContainer = divWithStyle('titleBarContainer', {
  paddingTop: TitleBarPaddingTop,
  paddingBottom: TitleBarPaddingBottom,
  paddingLeft: Spacing.MediumSpace,
  paddingRight: Spacing.MediumSpace,
  height: TitleBarHeight,
  display: 'flex',
  justifyContent: 'space-between',
  // Common header styles
  borderBottomColor: Colors.F5,
  borderBottomStyle: 'solid',
  borderBottomWidth: TitleBarBorderBottomWidth,
  backgroundColor: Colors.F1
});

export const TitleTextCssClass = cssClass('titleText', {
  paddingLeft: Spacing.DefaultSpace,
  alignSelf: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: Typography.Sizing.Default,
  fontWeight: 'normal',
  marginBottom: '0px',
  marginRight: 'auto',
  marginTop: '0px'
});

const lineagePanelBodyStyles: Style = {
  ...userSelectStyle,
  paddingTop: Spacing.LargeSpace,
  lineHeight: 1.5, // Make icons align nicely with multiline text
  position: 'absolute',
  top: TitleBarHeight + TitleBarPaddingTop + TitleBarPaddingBottom + TitleBarBorderBottomWidth,
  bottom: 0,
  left: 0,
  right: 0,
  overflowX: 'visible'
};

export const lineagePanelBody: string = cssClass('lineagePanelBody', lineagePanelBodyStyles);

export const lineagePanelBodyWithError: string = cssClass('lineagePanelBodyWithError', {
  ...lineagePanelBodyStyles,
  // Special one-off top margin per UX design
  paddingLeft: Spacing.LargeSpace,
  paddingRight: Spacing.LargeSpace,
  marginTop: '100px',
  textAlign: 'center'
});

export const HeaderWrapper = divWithStyle('headerWrapper', {
  paddingLeft: dataDetailsGutterWidth,
  paddingRight: dataDetailsGutterWidth,
  width: dataDetailsContentWidth,
});

export const SparklineHeader = divWithStyle('sparklineHeader', {
  color: Typography.Color.Primary,
  marginBottom: Spacing.DefaultSpace,
});

export const FieldsSectionHeader = divWithStyle('fieldsSectionHeader', {
  color: Typography.Color.Primary,
  marginBottom: Spacing.MediumSpace,
}, { role: 'heading', 'aria-level': 4 });

export const DatasourcesSectionHeader = divWithStyle('datasourcesSectionHeader', {
  color: Typography.Color.Primary,
  paddingBottom: Spacing.DefaultSpace,
}, { role: 'heading', 'aria-level': 4 });

export const DatasourceListBody = divWithStyle('DatasourceListBody', {
  overflowY: 'auto',
  overflowX: 'hidden', // set explicitly for IE11
  paddingRight: dataDetailsGutterWidth,
});

export const DatasourceListBodyElement = divWithStyle('DatasourceListBodyElement', {
  border: `${Sizing.BorderWidth}px solid ${Colors.L6}`,
  padding: Spacing.DefaultSpace,
  marginTop: Spacing.DefaultSpace,
  marginBottom: Spacing.MediumSpace,
  borderRadius: 1,
});

export const LabelIconWrapper = divWithStyle('LabelIconWrapper', {
  paddingTop: 2
});

export const DatasourceIconNameContainer = divWithStyle('DatasourceIconNameContainer', {
  display: 'flex',
  flexDirection: 'row',
  paddingBottom: Spacing.DefaultSpace
});

export const SheetFieldsListBody = divWithStyle('SheetFieldsListBody', {});

export const SheetFieldListElementContainer = divWithStyle('SheetFieldListElementContainer', {
  paddingBottom: Spacing.SmallSpace,
});

export const DetailsPaneSection = divWithStyle('DetailsPaneSection', {
  flex: 'none',
  display: 'flex',
  flexDirection: 'column', // Defining the FlexDirection is necessary so that the child can fill the height
  paddingBottom: Spacing.ExtraLargeSpace,
  paddingLeft: dataDetailsGutterWidth,
  paddingTop: Spacing.LargeSpace,
  paddingRight: dataDetailsGutterWidth,
  width: dataDetailsContentWidth
});

export const DatasourceListSection = divWithStyle('DatasourceListSection', {
  flex: 'none',
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: dataDetailsGutterWidth,
  paddingTop: Spacing.LargeSpace,
  borderTop: `1px solid ${Colors.L6}`,
  borderBottom: `1px solid ${Colors.L6}`,
  marginTop: Spacing.MediumSpace,
  overflow: 'visible'
});
